import {
  Alert,
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiErrorAlert } from "./ApiErrorAlert.js";
import {
  useAssumableEmployees,
  useStartViewAs,
} from "../hooks/useViewAs.js";

/**
 * Auswahl der Person, deren Sicht übernommen wird.
 *
 * Die Liste kommt vom Server und trägt dort bereits die Reichweiten- und
 * Aufstiegsregeln — die Oberfläche filtert nichts nach, sie zeigt nur, was
 * erlaubt ist. Wäre es umgekehrt, hinge die Rechtegrenze an einer
 * Anzeigebedingung.
 */
export function ViewAsPicker({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [suche, setSuche] = useState("");
  const { data, isLoading, error } = useAssumableEmployees(opened);
  const start = useStartViewAs();

  const gefiltert = useMemo(() => {
    const alle = data?.candidates ?? [];
    const q = suche.trim().toLowerCase();
    if (!q) return alle;
    return alle.filter(
      (k) =>
        k.fullName.toLowerCase().includes(q) ||
        k.email.toLowerCase().includes(q),
    );
  }, [data, suche]);

  return (
    <Modal opened={opened} onClose={onClose} title={t("viewAs.pickerTitle")}>
      <Stack gap="sm">
        <Alert color="yellow" variant="light">
          {t("viewAs.pickerWarning")}
        </Alert>

        {error ? <ApiErrorAlert error={error} /> : null}
        {start.error ? <ApiErrorAlert error={start.error} /> : null}

        <TextInput
          placeholder={t("viewAs.searchPlaceholder")}
          value={suche}
          onChange={(e) => setSuche(e.currentTarget.value)}
        />

        {isLoading ? <Text size="sm">{t("common.loading")}</Text> : null}

        {!isLoading && gefiltert.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("viewAs.noCandidates")}
          </Text>
        ) : null}

        <ScrollArea.Autosize mah={320}>
          <Stack gap={4}>
            {gefiltert.map((k) => (
              <UnstyledButton
                key={k.employeeId}
                p="xs"
                disabled={start.isPending}
                onClick={() =>
                  start.mutate(k.employeeId, { onSuccess: () => onClose() })
                }
                style={{ borderRadius: 4 }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text size="sm">{k.fullName}</Text>
                    <Text size="xs" c="dimmed">
                      {k.email}
                    </Text>
                  </div>
                  <Button
                    size="compact-xs"
                    variant="light"
                    component="span"
                    loading={start.isPending}
                  >
                    {t("viewAs.assume")}
                  </Button>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  );
}
